import { useEffect, useState } from 'react';
import { Play, Square } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { toLocalInputValue, fromLocalInputValue } from '../../utils/trips';
import type { Trip } from '../../types/trip';

// #190 — Iniciar/Finalizar estampaban SIEMPRE `now()`, así que no había forma
// de registrar un viaje ya realizado: declarar una salida/llegada pasada chocaba
// con la ventana y el core devolvía "la ventana del viaje termina antes de
// empezar". El contrato ya aceptaba `at` en `/start` y `/finish`; este modal lo
// expone. Por defecto el campo arranca en "ahora" (el caso normal: se pulsa
// cuando ocurre); el supervisor puede retroceder la hora para hacer backfill.
//
// Retrodatar es solo de Administrador/Supervisor (`canBackdate`): un Operador
// solo registra "ahora", sin editar la hora, para que no declare salidas o
// llegadas que no ocurrieron. Con `canBackdate` en false no se envía `at`, así
// que el instante lo pone el servidor (`now()`), a prueba del reloj del cliente.
interface TripMomentModalProps {
  isOpen: boolean;
  accion: 'iniciar' | 'finalizar';
  trip: Trip | null;
  isLoading?: boolean;
  error?: string | null;
  // Solo Administrador/Supervisor pueden elegir una hora pasada. El Operador no.
  canBackdate?: boolean;
  onConfirm: (at?: string) => void;
  onClose: () => void;
}

function formatInstant(iso: string): string {
  return new Intl.DateTimeFormat('es-CO', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

export function TripMomentModal({ isOpen, accion, trip, isLoading, error, canBackdate = true, onConfirm, onClose }: TripMomentModalProps) {
  const [value, setValue] = useState('');

  // Al abrir, el campo arranca en "ahora". Depende de `trip?.id` y `accion`
  // para re-sembrarse si se encadena una acción con otra sin cerrar.
  useEffect(() => {
    if (isOpen) setValue(toLocalInputValue(new Date().toISOString()));
  }, [isOpen, trip?.id, accion]);

  if (!trip) return null;
  const esIniciar = accion === 'iniciar';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${esIniciar ? 'Iniciar' : 'Finalizar'} viaje · ${trip.vehiclePlate}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Button
            variant={esIniciar ? 'primary' : 'success'}
            leftIcon={esIniciar ? <Play size={14} /> : <Square size={14} />}
            onClick={() => onConfirm(canBackdate ? fromLocalInputValue(value) : undefined)}
            isLoading={isLoading}
            disabled={canBackdate && !value}
          >
            {esIniciar ? 'Iniciar' : 'Finalizar'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {canBackdate ? (
          <>
            <Input
              label={esIniciar ? 'Hora de salida real' : 'Hora de llegada real'}
              type="datetime-local"
              value={value}
              onChange={e => setValue(e.target.value)}
            />
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
              Déjala en la hora actual para registrar que ocurre ahora, o elige una hora pasada
              para registrar un viaje ya realizado.
            </p>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
            Se registrará con la hora actual.
          </p>
        )}
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)' }}>
          Programado: {formatInstant(trip.scheduledFrom)} → {formatInstant(trip.scheduledTo)}
          {trip.actualFrom && <> · Salida real: {formatInstant(trip.actualFrom)}</>}
        </p>
        {error && <p style={{ margin: 0, fontSize: '12px', color: 'var(--danger, #dc2626)' }}>{error}</p>}
      </div>
    </Modal>
  );
}
